// SecPose annotation viewer — the configurable replacement for the bitmask
// <Viewer/> when FLOW === 'secpose'. It plugs into the EXISTING platform flow:
// AuthGate + Header + OperatorDashboard handle login / operator / assignment /
// lease and set `selectedEpisode` + `leaseId` in the platform store, then
// App.tsx renders this at mode==='viewer'. This component only owns the pose
// annotation surface: load /secpose-assets for the current episode, render the
// ported AnnotateView, and commit via /pose/commit.

import { type ReactElement, useEffect, useRef, useState } from 'react';
import { fetchJSON, HttpError } from '../api';
import { apiPath } from '../constants';
import { useStore as usePlatformStore } from '../store';
import { showToast } from '../toast';
import { SecPoseDataErrorModal } from './components/SecPoseDataErrorModal';
import { deleteDraft } from './io/drafts';
import { buildSecPoseSourceDescriptor, type SecPoseDraftSource } from './io/encryptedDraft';
import { loadSecPoseAssets, type SecPoseAssetsResponse } from './io/loadAssets';
import { clearSecPoseImageCache, prefetchSecPoseImage } from './secposeImageCache';
import { useStore as usePoseStore } from './store';
import type { Side } from './store/predictions';
import AnnotateView from './views/AnnotateView';

const SIDES: Side[] = ['front_left', 'front_right', 'left', 'right'];
const PREFETCH_WINDOW = 2; // frames each direction, all 4 cameras

// Split an assets-load failure into a human-readable reason and whether it is a
// permanent data defect. The worker returns 404 with a `{detail}` body when the
// episode's calibration/estimations are missing in R2 — those can never be
// annotated, so we release (like previewDurationMismatch). Everything else
// (network, 500, auth) may be transient, so it keeps the retry/back path.
function classifyLoadError(err: unknown): { reason: string; releasable: boolean } {
  const rawMessage = err instanceof Error ? err.message : String(err);
  let reason = rawMessage;
  try {
    const parsed = JSON.parse(rawMessage) as { detail?: unknown };
    if (typeof parsed.detail === 'string') reason = parsed.detail;
  } catch {
    // message isn't a JSON error body — use it verbatim.
  }
  return { reason, releasable: err instanceof HttpError && err.status === 404 };
}

export function SecPoseViewer(): ReactElement {
  // Assignment + lease + operator come from the platform store (set by the
  // shared OperatorDashboard → startOperator/AuditRandom flow).
  const episodeId = usePlatformStore((s) => s.selectedEpisode);
  const leaseId = usePlatformStore((s) => s.leaseId);
  const operator = usePlatformStore((s) => s.operator);
  // viewerMode is computed fresh from the episode's real mark_status by
  // openEpisode/startOperatorRandom/startAuditRandom for every entry point
  // (Random, Load Episode, History review, admin list) — mirrors how the
  // bitmask Viewer derives annotate-vs-audit, instead of guessing from
  // whichever section the user navigated from.
  const viewerMode = usePlatformStore((s) => s.viewerMode);
  const rejectionMessage = usePlatformStore((s) => s.rejectionMessage);
  const kind = viewerMode === 'auditing' ? 'audit' : 'annotation';
  const resetAnnotationSession = usePlatformStore((s) => s.resetAnnotationSession);
  const showOperatorDashboard = usePlatformStore((s) => s.showOperatorDashboard);
  const resetSecPose = usePoseStore((s) => s.resetSecPose);
  const releaseActiveLease = usePlatformStore((s) => s.releaseActiveLease);
  // Pose-store frame state, for windowed image prefetch.
  const poseFrame = usePoseStore((s) => s.currentFrame);
  const navigableFrames = usePoseStore((s) => s.navigableFrames);
  const imageUrls = usePoseStore((s) => s.imageUrls);

  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set only for permanent data defects (missing calibration/images): the
  // modal offers a single release-and-exit action instead of a bare retry.
  const [dataErrorReason, setDataErrorReason] = useState<string | null>(null);
  const [releasing, setReleasing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [draftSource, setDraftSource] = useState<SecPoseDraftSource | null>(null);
  const startedAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (!episodeId || viewerMode === 'rejecting') return;
    let cancelled = false;
    setReady(false);
    setError(null);
    setDataErrorReason(null);
    setDraftSource(null);
    clearSecPoseImageCache(); // drop the previous episode's frames
    void (async () => {
      try {
        const assets = await fetchJSON<SecPoseAssetsResponse>(
          apiPath(`/episode/${encodeURIComponent(episodeId)}/secpose-assets`)
        );
        if (!assets) throw new Error('Empty secpose-assets response');
        const loaded = await loadSecPoseAssets(assets);
        const source = await buildSecPoseSourceDescriptor(assets, loaded.poseSeedSource);
        if (cancelled) return;
        setDraftSource(source);
        startedAtRef.current = Date.now();
        setReady(true);
      } catch (err) {
        if (cancelled) return;
        const { reason, releasable } = classifyLoadError(err);
        if (releasable) {
          setDataErrorReason(reason);
        } else {
          setError(reason);
          showToast(reason);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [episodeId, viewerMode]);

  // Windowed prefetch: warm current ± window frames across all 4 cameras so
  // scrubbing is smooth. The cache (secposeImageCache) caps + evicts, so this
  // never accumulates the whole episode.
  useEffect(() => {
    if (!ready || !navigableFrames) return;
    const idx = navigableFrames.indexOf(poseFrame);
    if (idx < 0) return;
    for (let d = -PREFETCH_WINDOW; d <= PREFETCH_WINDOW; d++) {
      const frame = navigableFrames[idx + d];
      if (frame == null) continue;
      for (const side of SIDES) prefetchSecPoseImage(imageUrls[side]?.[frame]);
    }
  }, [ready, poseFrame, navigableFrames, imageUrls]);

  // Revoke all cached blobs when the viewer unmounts.
  useEffect(() => () => clearSecPoseImageCache(), []);

  function backToDashboard(): void {
    resetSecPose();
    resetAnnotationSession();
    showOperatorDashboard();
  }

  // Defective episode: drop the lease (annotate vs audit) so it returns to the
  // queue, then go home. Mirrors acknowledgePreviewDurationMismatch in store.ts.
  async function releaseAndExit(): Promise<void> {
    setReleasing(true);
    try {
      await releaseActiveLease(kind === 'audit' ? 'audit' : 'annotate');
    } finally {
      setReleasing(false);
      backToDashboard();
    }
  }

  async function handleSubmit(
    toonText: string,
    frameCount: number,
    elapsedMs: number
  ): Promise<void> {
    if (!episodeId) throw new Error('No Episode is selected.');
    if (!leaseId?.trim()) {
      throw new Error('Claim this Episode before submitting.');
    }
    if (viewerMode !== 'annotating' && viewerMode !== 'auditing') {
      throw new Error('This Episode is open in read-only mode.');
    }
    setSubmitting(true);
    try {
      const sessionDurationMs = Number.isFinite(elapsedMs)
        ? elapsedMs
        : startedAtRef.current
          ? Date.now() - startedAtRef.current
          : null;
      await fetchJSON(apiPath(`/pose/commit/${encodeURIComponent(episodeId)}`), {
        method: 'post',
        json: {
          operator,
          lease_id: leaseId,
          kind,
          session_duration_ms: sessionDurationMs,
          pose: { adjusted_toon: toonText, frame_count: frameCount },
        },
      });
      try {
        await deleteDraft(episodeId);
      } catch (err) {
        console.warn('secPose draft delete failed:', err);
      }
      showToast('Pose Adjust submitted');
      backToDashboard();
    } finally {
      setSubmitting(false);
    }
  }

  function handlePending(): void {
    showToast('Pose Adjust progress saved');
    backToDashboard();
  }

  if (!episodeId) {
    return (
      <div className="flex items-center justify-center py-32 text-brand-text-secondary">
        No episode selected.
      </div>
    );
  }
  if (viewerMode === 'rejecting') {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-32 text-center">
        <p className="text-brand-text-secondary max-w-md">{rejectionMessage}</p>
        <button
          type="button"
          className="rounded-md border border-brand-border px-4 py-2 text-brand-text-primary"
          onClick={backToDashboard}
        >
          Back
        </button>
      </div>
    );
  }
  if (dataErrorReason) {
    return (
      <SecPoseDataErrorModal
        reason={dataErrorReason}
        releasing={releasing}
        onRelease={() => void releaseAndExit()}
      />
    );
  }
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-32 text-center">
        <p className="text-brand-text-secondary">Failed to load: {error}</p>
        <button
          type="button"
          className="rounded-md border border-brand-border px-4 py-2 text-brand-text-primary"
          onClick={backToDashboard}
        >
          Back
        </button>
      </div>
    );
  }
  if (!ready) {
    return (
      <div className="flex items-center justify-center gap-3 py-32 text-brand-text-secondary">
        <span className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
        <span className="text-sm">Loading {episodeId}…</span>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-3rem)]">
      <AnnotateView
        onSubmit={handleSubmit}
        onPending={handlePending}
        onQuit={backToDashboard}
        submitting={submitting}
        canSubmit={Boolean(leaseId) && (viewerMode === 'annotating' || viewerMode === 'auditing')}
        submitBlockedReason={
          viewerMode === 'reviewing'
            ? 'This Episode is open in read-only mode.'
            : 'Claim this Episode before submitting.'
        }
        draftTransfer={
          draftSource && viewerMode === 'annotating'
            ? {
                episodeId,
                operator,
                leaseId,
                editable: true,
                source: draftSource,
              }
            : undefined
        }
      />
    </div>
  );
}
